import { useCallback, useEffect, useMemo, useState } from "react";
import { Feature, MultiPolygon, Polygon } from "geojson";

import { useDataset } from "fond/hooks/useDataset";
import { isAnyPolygon } from "fond/types/geojson";

interface UseBlockGroupsReturn {
  isLoading: boolean;
  blockGroups: Feature<Polygon | MultiPolygon>[] | null;
  resetBlockGroups: () => void;
}

export const useBlockGroups = (accountId: string | null, boundary: Polygon | MultiPolygon | null): UseBlockGroupsReturn => {
  const { isLoading: isDatasetLoading, featuresWithin: datasetFeaturesWithin } = useDataset({
    key: "census-block-groups-2024",
    accountId,
  });

  const [blockGroups, setBlockGroups] = useState<Feature<Polygon | MultiPolygon>[] | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!datasetFeaturesWithin || !boundary) return;
      const fetchedBlockGroups = await datasetFeaturesWithin(boundary);
      setBlockGroups(fetchedBlockGroups.features.filter(isAnyPolygon));
    };

    // silence 'promise returned from load() is ignored' warning with pointless .then()
    load().then();
  }, [datasetFeaturesWithin, boundary]);

  const resetBlockGroups = useCallback(() => {
    setBlockGroups(null);
  }, []);

  return useMemo(() => ({ isLoading: isDatasetLoading, blockGroups, resetBlockGroups }), [isDatasetLoading, blockGroups, resetBlockGroups]);
};
